import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ChevronUp, LogOut, type LucideIcon } from "lucide-react";

declare const __APP_VERSION__: string | undefined;
/** Release identifier embedded at build time (package version + revision). */
export const applicationVersion =
  typeof __APP_VERSION__ === "string" && __APP_VERSION__
    ? __APP_VERSION__
    : "development";

/** Account controls the workspace shell anchors at the bottom of its navigation. */
export const WorkspaceAccountContext = createContext<ReactNode>(null);
export const useWorkspaceAccount = () => useContext(WorkspaceAccountContext);

export interface AccountMenuItem {
  label: string;
  Icon: LucideIcon;
  current?: boolean;
  onSelect: () => void;
}

export function AccountMenu({
  name,
  detail,
  items,
  onSignOut,
  placement = "up",
}: {
  name: string;
  detail?: string;
  items: AccountMenuItem[];
  onSignOut?: () => void;
  /** "up" opens above the trigger (sidebar), "down" below it (header). */
  placement?: "up" | "down";
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null),
    trigger = useRef<HTMLButtonElement>(null),
    menu = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  useEffect(() => {
    if (!open) return;
    menu.current
      ?.querySelector<HTMLElement>('[role="menuitem"][aria-current="page"]')
      ?.focus();
    if (!menu.current?.contains(document.activeElement))
      menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const pointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", pointer);
    return () => document.removeEventListener("pointerdown", pointer);
  }, [open]);
  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) trigger.current?.focus();
  };
  const move = (delta: number) => {
    const entries = [
      ...(menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ??
        []),
    ];
    if (!entries.length) return;
    const index = entries.indexOf(document.activeElement as HTMLElement);
    entries[(index + delta + entries.length) % entries.length].focus();
  };
  return (
    <div className={`account-menu account-menu-${placement}`} ref={root}>
      <div className="account-menu-row">
        <button
          ref={trigger}
          type="button"
          className="account-menu-trigger"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          title="Account menu"
          onClick={() => setOpen((value) => !value)}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault();
              setOpen(true);
            }
          }}
        >
          <span className="account-menu-initials" aria-hidden="true">
            {initials}
          </span>
          <span className="account-menu-name">
            <strong>{name}</strong>
            {detail && <small>{detail}</small>}
          </span>
          <ChevronUp className="account-menu-chevron" size={14} />
        </button>
        {onSignOut && (
          <button
            type="button"
            className="icon-button account-sign-out"
            aria-label="Sign out"
            title="Sign out"
            onClick={onSignOut}
          >
            <LogOut size={16} />
          </button>
        )}
      </div>
      {open && (
        <div
          ref={menu}
          id={menuId}
          role="menu"
          aria-label="Account"
          className="account-menu-popover"
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              close();
            } else if (event.key === "ArrowDown") {
              event.preventDefault();
              move(1);
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              move(-1);
            } else if (event.key === "Tab") close(false);
          }}
        >
          {items.map(({ label, Icon, current, onSelect }) => (
            <button
              key={label}
              type="button"
              role="menuitem"
              aria-current={current ? "page" : undefined}
              onClick={() => {
                close();
                onSelect();
              }}
            >
              <Icon size={16} aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>
      )}
      <p className="account-menu-version">Version {applicationVersion}</p>
    </div>
  );
}
